import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Table, Icon, Text, IconButton, Stack } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { prettifyDate } from '~/subscription-management/utils/date.utils';
import type { InvoiceItemStatus } from './invoice-list.types';
import { useDownloadInvoice } from './useDownloadInvoice';

interface InvoiceItem {
  issueDate: string;
  total: string;
  id?: string;
  status: InvoiceItemStatus;
  link?: string;
}

const S = {
  Cell: styled(Table.Cell)`
    vertical-align: middle;
  `,
  Download: styled(Table.Cell)`
    vertical-align: middle;
    width: 50px;
  `,
};

export function InvoiceListItem({ total, issueDate, id, status, link }: InvoiceItem): JSX.Element {
  const { t } = useTranslationWithPrefix('checkout:invoices.invoice-list');
  const { download, isDownloading } = useDownloadInvoice({ errorMessage: t('download-error') });

  const date = prettifyDate(issueDate);
  const getStatusData = (invoiceStatus: InvoiceItemStatus) => {
    switch (invoiceStatus) {
      case 'paid':
        return { text: t('paid'), icon: 'check', color: 'text_success' } as const;
      case 'next':
        return { text: t('status-next'), icon: 'time_back', color: 'text_default' } as const;
      default:
        return { text: t('unpaid'), icon: 'time_back', color: 'text_danger' } as const;
    }
  };

  const statusData = getStatusData(status);

  return (
    <Table.Row key={id}>
      <S.Cell>
        <Stack
          spacing="x0pt5"
          css={css`
            align-items: center;
          `}
        >
          <Icon icon={statusData.icon} color={statusData.color} />
          <Text textType="body2" textColor={statusData.color}>
            {statusData.text}
          </Text>
        </Stack>
      </S.Cell>
      <S.Cell>{date}</S.Cell>
      <S.Cell>{total}</S.Cell>
      <S.Download>
        {link !== undefined && (
          <IconButton
            kind="ghost"
            onClick={() => download(link)}
            tooltipText={t('download')}
            title={t('download')}
            isLoading={isDownloading}
            icon="download"
          />
        )}
      </S.Download>
    </Table.Row>
  );
}
